import React, { useState, useEffect } from 'react';
import { Dictionary, apiService } from '../services/api';
import { topicCategories, getMaxPairsInDictionary, builtInDictionaries } from '../builtInDictionaries';

interface TeacherClass {
  id: number;
  name: string;
  description: string;
}

interface Assignment {
  id: number;
  name: string;
  description: string;
  requirements: any;
  due_date: string;
  created_at: string;
  updated_at: string;
  class_name: string;
  class_id: number;
  game_count: number;
  student_count: number;
  completed_count: number;
}

const TeacherAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [assignmentName, setAssignmentName] = useState('');
  const [assignmentDescription, setAssignmentDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [requirements, setRequirements] = useState('');
  const [selectedGames, setSelectedGames] = useState<Array<{ dictionaryId?: number; builtInGameId?: string; name: string; pairCount?: number; maxPairs?: number; matchingMode?: 'one-to-one' | 'category' }>>([]);

  useEffect(() => {
    loadAssignments();
    loadClasses();
    loadDictionaries();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTeacherAssignments();
      if (data.success) {
        setAssignments(data.assignments);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await apiService.getTeacherClasses();
      if (data.success) {
        setClasses(data.classes);
      }
    } catch (err: any) {
      console.error('Failed to load classes:', err);
    }
  };

  const loadDictionaries = async () => {
    try {
      const data = await apiService.getDictionaries();
      // Handle both formats: direct array or { dictionaries: [...] }
      const dicts = Array.isArray(data) ? data : ((data as any).dictionaries || []);
      setDictionaries(dicts);
    } catch (err: any) {
      console.error('Failed to load dictionaries:', err);
      setDictionaries([]); // Set empty array on error to prevent .map() errors
    }
  };

  const handleCreateAssignment = async () => {
    if (!selectedClassId) {
      setError('Please select a class');
      return;
    }
    if (!assignmentName.trim()) {
      setError('Assignment name is required');
      return;
    }
    if (selectedGames.length === 0) {
      setError('Please add at least one game');
      return;
    }

    try {
      const games = selectedGames.map(g => ({
        dictionaryId: g.dictionaryId,
        builtInGameId: g.builtInGameId,
        pairCount: g.pairCount || 5,
        matchingMode: g.matchingMode || 'one-to-one',
      }));

      let parsedRequirements = null;
      if (requirements.trim()) {
        try {
          parsedRequirements = JSON.parse(requirements);
        } catch {
          parsedRequirements = { notes: requirements };
        }
      }

      // Convert datetime-local to ISO string preserving local timezone
      let dueDateWithTimezone = undefined;
      if (dueDate) {
        const localDate = new Date(dueDate);
        // Get timezone offset and format as ISO string with timezone
        const tzOffset = -localDate.getTimezoneOffset();
        const sign = tzOffset >= 0 ? '+' : '-';
        const hours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
        const minutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
        dueDateWithTimezone = `${dueDate}:00${sign}${hours}:${minutes}`;
      }

      const data = await apiService.createAssignment({
        classId: Number(selectedClassId),
        name: assignmentName,
        description: assignmentDescription || undefined,
        requirements: parsedRequirements,
        dueDate: dueDateWithTimezone,
        games,
      });

      if (data.success) {
        setShowCreateForm(false);
        resetForm();
        await loadAssignments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create assignment');
    }
  };

  const handleEditAssignment = async (assignmentId: number) => {
    try {
      // Load assignment details
      const data = await apiService.getAssignmentDetails(assignmentId);
      if (data.success && data.assignment) {
        const assignment = data.assignment;

        // Populate form with assignment data
        setEditingAssignmentId(assignmentId);
        setSelectedClassId(assignment.class_id);
        setAssignmentName(assignment.name);
        setAssignmentDescription(assignment.description || '');
        // Convert due_date to local datetime-local format without timezone shift
        if (assignment.due_date) {
          const date = new Date(assignment.due_date);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          setDueDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        } else {
          setDueDate('');
        }
        setRequirements(typeof assignment.requirements === 'string' ? assignment.requirements : JSON.stringify(assignment.requirements || '', null, 2));

        // Load games with their data
        const loadedGames = [];
        for (const game of assignment.games) {
          if (game.built_in_game_id) {
            const builtInGame = builtInDictionaries.find(g => g.id === game.built_in_game_id);
            if (builtInGame) {
              const maxPairs = getMaxPairsInDictionary(builtInGame.data);
              let foundGame = null;
              for (const topic of topicCategories) {
                const g = topic.games.find(tg => tg.id === game.built_in_game_id);
                if (g) {
                  foundGame = g;
                  break;
                }
              }
              if (foundGame) {
                loadedGames.push({
                  builtInGameId: game.built_in_game_id,
                  name: `${foundGame.icon} ${foundGame.name}`,
                  pairCount: game.pair_count || 5,
                  maxPairs
                });
              }
            }
          } else if (game.dictionary_id) {
            const dict = dictionaries.find(d => d.id === game.dictionary_id);
            if (dict) {
              const maxPairs = getMaxPairsInDictionary(dict.data);
              loadedGames.push({
                dictionaryId: game.dictionary_id,
                name: dict.name,
                pairCount: game.pair_count || 5,
                maxPairs
              });
            }
          }
        }
        setSelectedGames(loadedGames);
        setShowCreateForm(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignment for editing');
    }
  };

  const handleUpdateAssignment = async () => {
    if (!editingAssignmentId) return;

    if (!selectedClassId) {
      setError('Please select a class');
      return;
    }
    if (!assignmentName.trim()) {
      setError('Assignment name is required');
      return;
    }
    if (selectedGames.length === 0) {
      setError('Please add at least one game');
      return;
    }

    try {
      const games = selectedGames.map(g => ({
        dictionaryId: g.dictionaryId,
        builtInGameId: g.builtInGameId,
        pairCount: g.pairCount || 5,
        matchingMode: g.matchingMode || 'one-to-one',
      }));

      let parsedRequirements = null;
      if (requirements.trim()) {
        try {
          parsedRequirements = JSON.parse(requirements);
        } catch {
          parsedRequirements = { notes: requirements };
        }
      }

      // Convert datetime-local to ISO string preserving local timezone
      let dueDateWithTimezone = undefined;
      if (dueDate) {
        const localDate = new Date(dueDate);
        // Get timezone offset and format as ISO string with timezone
        const tzOffset = -localDate.getTimezoneOffset();
        const sign = tzOffset >= 0 ? '+' : '-';
        const hours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
        const minutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
        dueDateWithTimezone = `${dueDate}:00${sign}${hours}:${minutes}`;
      }

      const data = await apiService.updateAssignment(editingAssignmentId, {
        name: assignmentName,
        description: assignmentDescription || undefined,
        requirements: parsedRequirements,
        dueDate: dueDateWithTimezone,
        games,
      });

      if (data.success) {
        setShowCreateForm(false);
        resetForm();
        setEditingAssignmentId(null);
        await loadAssignments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    if (!window.confirm('Delete this assignment? This cannot be undone.')) {
      return;
    }

    try {
      const data = await apiService.deleteAssignment(assignmentId);
      if (data.success) {
        await loadAssignments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete assignment');
    }
  };

  const addBuiltInGame = (gameId: string) => {
    // Find the game in builtInDictionaries to get max pairs
    const builtInGame = builtInDictionaries.find(g => g.id === gameId);
    if (!builtInGame) return;

    const maxPairs = getMaxPairsInDictionary(builtInGame.data);
    const defaultPairCount = Math.min(5, maxPairs);

    // Detect matching mode from built-in game data
    const matchingMode = (builtInGame.data as any)?.metadata?.matchingMode === 'category' ? 'category' : 'one-to-one';

    // Find the game in topicCategories for display name
    let foundGame = null;
    for (const topic of topicCategories) {
      const game = topic.games.find(g => g.id === gameId);
      if (game) {
        foundGame = game;
        break;
      }
    }

    if (foundGame) {
      setSelectedGames([...selectedGames, {
        builtInGameId: gameId,
        name: `${foundGame.icon} ${foundGame.name}`,
        pairCount: defaultPairCount,
        maxPairs: maxPairs,
        matchingMode: matchingMode as 'one-to-one' | 'category'
      }]);
    }
  };

  const addDictionaryGame = (dictId: number) => {
    const dict = dictionaries.find(d => d.id === dictId);
    if (dict) {
      const maxPairs = getMaxPairsInDictionary(dict.data);
      const defaultPairCount = Math.min(5, maxPairs);
      // Detect matching mode from dictionary data
      const matchingMode = (dict.data as any)?.metadata?.matchingMode === 'category' ? 'category' : (dict.matching_mode || 'one-to-one');
      setSelectedGames([...selectedGames, {
        dictionaryId: dictId,
        name: dict.name,
        pairCount: defaultPairCount,
        maxPairs: maxPairs,
        matchingMode: matchingMode as 'one-to-one' | 'category'
      }]);
    }
  };

  const removeGame = (index: number) => {
    setSelectedGames(selectedGames.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setSelectedClassId('');
    setAssignmentName('');
    setAssignmentDescription('');
    setDueDate('');
    setRequirements('');
    setSelectedGames([]);
    setEditingAssignmentId(null);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No deadline';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const isOverdue = (dateStr: string) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return <div className="teacher-assignments">Loading assignments...</div>;
  }

  return (
    <div className="teacher-assignments">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Assignments List View */}
      {!showCreateForm && (
        <div className="assignments-list-view">
          <div className="assignments-header">
            <h3>📝 My Assignments</h3>
            <button onClick={() => setShowCreateForm(true)} className="create-assignment-btn">
              ➕ Create New Assignment
            </button>
          </div>

          {classes.length === 0 && (
            <div className="warning-message">
              <p>⚠️ You need to create a class first before creating assignments!</p>
            </div>
          )}

          {assignments.length === 0 ? (
            <div className="empty-state">
              <p>No assignments yet. Create your first assignment to get started!</p>
            </div>
          ) : (
            <div className="assignments-by-class">
              {/* Group assignments by class */}
              {classes.map(classItem => {
                const classAssignments = assignments.filter(a => a.class_id === classItem.id);
                if (classAssignments.length === 0) return null;

                return (
                  <div key={classItem.id} className="class-section">
                    <h3 className="class-section-header">
                      📚 {classItem.name}
                      <span className="class-assignment-count">({classAssignments.length} assignment{classAssignments.length !== 1 ? 's' : ''})</span>
                    </h3>
                    <div className="assignments-grid">
                      {classAssignments.map(assignment => (
                        <div key={assignment.id} className="assignment-card">
                          <div className="assignment-card-header">
                            <h4>{assignment.name}</h4>
                            <div className="assignment-card-actions">
                              <button
                                onClick={() => handleEditAssignment(assignment.id)}
                                title="Edit Assignment"
                                className="edit-btn"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteAssignment(assignment.id)}
                                title="Delete"
                                className="delete-btn"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>

                          {assignment.description && (
                            <p className="assignment-description">{assignment.description}</p>
                          )}

                          <div className="assignment-stats">
                            <span>🎮 {assignment.game_count} games</span>
                            <span>👥 {assignment.student_count} students</span>
                            <span>✅ {assignment.completed_count} completed</span>
                          </div>

                          <div className={`assignment-due-date ${isOverdue(assignment.due_date) ? 'overdue' : ''}`}>
                            📅 {formatDate(assignment.due_date)}
                            {isOverdue(assignment.due_date) && ' (OVERDUE)'}
                          </div>

                          <div className="assignment-created">
                            Created: {new Date(assignment.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Assignment Form */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="assignment-form">
          <div className="form-header">
            <button onClick={() => {
              setShowCreateForm(false);
              resetForm();
            }} className="back-btn">
              ← Back
            </button>
            <h3>{editingAssignmentId ? 'Edit Assignment' : 'Create New Assignment'}</h3>
          </div>

          <div className="form-group">
            <label>Class *</label>
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Select a class...</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Assignment Name *</label>
            <input
              type="text"
              value={assignmentName}
              onChange={(e) => setAssignmentName(e.target.value)}
              placeholder="e.g., Week 1 Vocabulary Practice"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={assignmentDescription}
              onChange={(e) => setAssignmentDescription(e.target.value)}
              placeholder="Optional description and instructions..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Due Date</label>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Requirements (Optional)</label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="E.g., minimum accuracy 80%, time limit 5 minutes, etc."
              rows={2}
            />
            <small>Can be plain text or JSON format</small>
          </div>

          <div className="form-group">
            <label>Games * ({selectedGames.length} selected)</label>

            {selectedGames.length > 0 && (
              <div className="selected-games-list">
                {selectedGames.map((game, index) => (
                  <div key={index} className="selected-game-item">
                    <div className="game-item-info">
                      <span className="game-name">
                        #{index + 1}: {game.name}
                        {game.matchingMode === 'category' && (
                          <span className="game-mode-badge category-mode">📂 Category</span>
                        )}
                      </span>
                      <div className="pair-count-input">
                        <label>Pairs required:</label>
                        <div className="pair-count-controls">
                          <button
                            type="button"
                            className="pair-count-btn decrement"
                            onClick={() => {
                              const newGames = [...selectedGames];
                              const current = newGames[index].pairCount || 5;
                              newGames[index].pairCount = Math.max(1, current - 1);
                              setSelectedGames(newGames);
                            }}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            max={game.maxPairs || 50}
                            value={game.pairCount || 5}
                            onChange={(e) => {
                              const newGames = [...selectedGames];
                              const val = parseInt(e.target.value) || 1;
                              newGames[index].pairCount = Math.min(val, game.maxPairs || 50);
                              setSelectedGames(newGames);
                            }}
                          />
                          <button
                            type="button"
                            className="pair-count-btn increment"
                            onClick={() => {
                              const newGames = [...selectedGames];
                              const current = newGames[index].pairCount || 5;
                              const maxPairs = game.maxPairs || 50;
                              newGames[index].pairCount = Math.min(maxPairs, current + 1);
                              setSelectedGames(newGames);
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            className="pair-count-btn set-max"
                            onClick={() => {
                              const newGames = [...selectedGames];
                              newGames[index].pairCount = game.maxPairs || 50;
                              setSelectedGames(newGames);
                            }}
                            title={`Set to max (${game.maxPairs || 50} pairs)`}
                          >
                            Max
                          </button>
                        </div>
                        <small className="pair-count-hint">Max: {game.maxPairs || 50} pairs</small>
                      </div>
                    </div>
                    <button onClick={() => removeGame(index)} className="remove-game-btn">
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="game-selectors">
              <div className="game-selector-section">
                <h4>Built-in Games by Topic</h4>
                {topicCategories.map(topic => (
                  <details key={topic.id} className="topic-category" open={topic.id === 'mathematics'}>
                    <summary className="topic-summary">
                      {topic.icon} {topic.name} ({topic.games.length} games)
                    </summary>
                    <div className="game-buttons">
                      {topic.games.map(game => (
                        <button
                          key={game.id}
                          onClick={() => addBuiltInGame(game.id)}
                          className="game-select-btn"
                        >
                          + {game.icon} {game.name}
                        </button>
                      ))}
                    </div>
                  </details>
                ))}
              </div>

              <div className="game-selector-section">
                <h4>My Saved Dictionaries</h4>
                {dictionaries.length === 0 ? (
                  <p className="no-games-text">No saved dictionaries available</p>
                ) : (
                  <div className="game-buttons">
                    {dictionaries.map(dict => (
                      <button
                        key={dict.id}
                        onClick={() => addDictionaryGame(dict.id!)}
                        className="game-select-btn"
                      >
                        + {dict.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={editingAssignmentId ? handleUpdateAssignment : handleCreateAssignment}
              disabled={!selectedClassId || !assignmentName || selectedGames.length === 0}
              className="submit-btn"
            >
              {editingAssignmentId ? 'Update Assignment' : 'Create Assignment'}
            </button>
            <button onClick={() => {
              setShowCreateForm(false);
              resetForm();
            }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAssignments;
